import React from 'react';
import { withRouter } from 'react-router-dom';
import workflowUtil from '../util/workflowUtil';

/**
 * Higher order component to wrap a step with workflow logic.
 *
 * @param WrappedComponent the component to wrap
 */
function withWorkflow(WrappedComponent) {
	
	// wrap the workflow HOC with a router HOC to get access to the history and location when updating the workflow step.
	return withRouter(class extends React.Component {
		/**
		 * Constructor sets instance variables and binds functions.
		 *
		 * @param props the properties
		 * @param props.userInfo the signed-in user info
		 * @param props.gearOrder the GEAR order
		 */
		constructor(props) {
			super(props);

			this.getWorkflowStep = this.getWorkflowStep.bind(this);
			this.updateStep = this.updateStep.bind(this);
			this.isStepValidForUser = this.isStepValidForUser.bind(this);
			this.isRequisitionComplete = this.isRequisitionComplete.bind(this);
		}

		/**
	     * Gets a workflow step by key and optional requisition id.
		 *
		 * @param stepKey the step key
		 * @param gearReqId the gear requisition id (optional)
	     */
	    getWorkflowStep(stepKey, gearReqId) {
			let reqId = gearReqId || null;
			let stepIndex = this.props.gearOrder.workflow.steps.findIndex(step => {
				return step.id === workflowUtil.workflowMetaData[stepKey].id && (reqId === null || reqId === step.gearReqId);
			});
			return this.props.gearOrder.workflow.steps[stepIndex];
	    }

		/**
	     * Finds the previous or next workflow step by key and direction and pushes
	     * the step's route into the Router history (thereby navigating to the new page)
		 *
		 * @param gearOrder The current gear order
	     * @param stepKey The key of the current step
	     * @param direction The direction to move. Can be empty, 'Next', or 'Back'
		 * @param gearReqId the gear requisition id (optional)
	     */
		updateStep(gearOrder, stepKey, direction, gearReqId) {
			// Use the passed in gearOrder unless it is empty, then use props
			gearOrder = gearOrder || this.props.gearOrder;
			let reqId = Number(gearReqId) || null;
	        let reqStep;
			if (direction === 'Next') {
				// SCR-6905: For PRC users working through a previous order, always jump to the 
				// confirmation page (Unless we just submitted confirmation and need to go to Summary)
				if (
					gearOrder.id &&
					this.props.userInfo?.type === 'prc' &&
					workflowUtil.isClientOrder(gearOrder) &&
					stepKey !== 'patient_confirmation'
				) {
					this.props.history.push({
						pathname: `/order/${gearOrder.id}/confirm`,
						search: this.props.location.search
					});
					return;
				}

	            // Find the current step's index
	            let stepIndex = gearOrder.workflow.steps.findIndex(step => {
					return step.id === workflowUtil.workflowMetaData[stepKey].id && (reqId === null || step.gearReqId === null || step.gearReqId === reqId);
				});

	            reqStep = gearOrder.workflow.steps.find((step, index) => {
					// find the first "Not Started" step after the 'current' step. Default to "Confirmation" if all steps have been actioned
					return (
						index > stepIndex &&
						this.isStepValidForUser(gearOrder, step) &&
						(reqId === null || step.gearReqId === null || step.gearReqId === reqId) &&
						(gearOrder.containsShipToOfficeRequisition || // Don't skip steps during Bulk Order
							step.status?.id === 1 || // "Not Started"
							step.id > 7 || // After "Insurance"
							(step.status?.id === 2 && this.props.userInfo?.type === 'prc')) // "Skipped"
						// TODO: Should we stop at "Panels" for myGDX when Medicare Ack is required?
					);
				});
	        } else if( direction === 'Back' ) {
	            // Find the previous applicable step
	            let stepIndexReversed = gearOrder.workflow.steps.slice().reverse().findIndex(step => {
					return step.id === workflowUtil.workflowMetaData[stepKey].id && (reqId === null || step.gearReqId === null || step.gearReqId === reqId);
				});

	            reqStep = gearOrder.workflow.steps.slice().reverse().find((step, index) => {
	                // copy and reverse the steps, then find the first applicable step after the 'current' step
	                return index > stepIndexReversed && this.isStepValidForUser(gearOrder, step)
					 	&& ( reqId === null || step.gearReqId === null || step.gearReqId === reqId );
	            });
	        }

	        let workflowStep;
			let nextStepReqId = gearReqId;
	        if( reqStep ) {
	            // find the workflowMetaData step by matching the reqStep id
	            for( var stepName in workflowUtil.workflowMetaData ) {
	                if(workflowUtil.workflowMetaData[stepName].id === reqStep.id) {
	                    workflowStep = workflowUtil.workflowMetaData[stepName];
	                    break;
	                }
	            }
				nextStepReqId = reqStep.gearReqId;
	        } else {
	            // No valid direction was passed in or no next/previous step was found.
	            if( direction === 'Back' ) {
	                // Can't find the step to go back to. Go to the first step (/search)
	                this.props.history.push({pathname:'/'});
	                return;
	            }
	            // Find the step for the given stepKey
	            workflowStep = workflowUtil.workflowMetaData[stepKey];
	        }

	        let route;
	        if( workflowStep ) {
	            route = workflowStep.route;
	        }

	        if( route ) {
	            this.props.history.push({
					pathname:'/order/' + (gearOrder.id || "new") + "/" + route + (nextStepReqId ? '/' + nextStepReqId : ''),
					search: this.props.location.search
				});
	        } else {
	            // Couldn't determine step from stepKey. Use the stepKey directly (legacy support)
	            this.props.history.push({
					pathname:'/'+stepKey+'/'+gearOrder.id + (nextStepReqId ? '/' + nextStepReqId : ''),
					search: this.props.location.search
				});
	        }
	    }
		
		/**
		 * Checks if the given step is valid for the current user type.
		 * For example, a 'prc' user should never see the 'Practitioner Confirmation' step.
		 */
		isStepValidForUser(gearOrder, workflowStep) {
			return workflowUtil.isStepValidForUser(workflowStep, this.props.userInfo, gearOrder || this.props.gearOrder);
		}
		
		/**
		 * Determines if an individual requisition on the order is completed (all order-level steps and steps for this 
		 * req are completed or n/a).
		 *
		 * @param gearReqId the gear requisition id
		 */
		isRequisitionComplete(gearReqId) {
			return this.props.gearOrder.workflow.steps
				.filter(
					(step) =>
						step.id !== workflowUtil.workflowMetaData.summary.id &&
						step.id !== workflowUtil.workflowMetaData.patient_confirmation.id &&
						step.id !== workflowUtil.workflowMetaData.practitioner_summary.id &&
						step.id !== workflowUtil.workflowMetaData.handoff.id &&
						step.id !== workflowUtil.workflowMetaData.billing.id
				)
				.every(
					(step) =>
						step.completed ||
						step.notApplicable ||
						(step.gearReqId !== null && step.gearReqId !== gearReqId)
				);
		}
		
		render() {
			return (
				<WrappedComponent
					workflowMetaData={workflowUtil.workflowMetaData}
			 		getWorkflowStep={this.getWorkflowStep}
					updateStep={this.updateStep}
					isStepValidForUser={this.isStepValidForUser}
					isRequisitionComplete={this.isRequisitionComplete}
					{...this.props} />
			);
		}
	});
}

export default withWorkflow;
